import {t} from "@lingui/core/macro";
import {StyleSheet} from "aphrodite";
import * as React from "react";

import Button from "@khanacademy/wonder-blocks-button";
import {View} from "@khanacademy/wonder-blocks-core";
import {Strut} from "@khanacademy/wonder-blocks-layout";
import {OnePaneDialog} from "@khanacademy/wonder-blocks-modal";
import {spacing} from "@khanacademy/wonder-blocks-tokens";
import {Body} from "@khanacademy/wonder-blocks-typography";
import {
    useArchiveDocumentMutation,
    useDeleteBlooketQuestionSetMutation,
} from "@khan/khanmigo-tools";

import {renderErrorToast} from "#notifications-package/toast-notification.tsx";

type Props = {
    closeModal: (archived: boolean) => void;
    threadId: string;
    documentName: string;
    configName: string;
};

function MyDocumentsArchiveModal({
    closeModal,
    threadId,
    documentName,
    configName,
}: Props): React.ReactElement {
    const archiveDocumentMutation = useArchiveDocumentMutation();

    const deleteBlooketQuestionSetMutation =
        useDeleteBlooketQuestionSetMutation();

    const handleArchive = async (threadID: string, configName: string) => {
        if (configName === "teacher-tool-blooket-generator") {
            const result = await deleteBlooketQuestionSetMutation({
                threadId: threadID,
            });

            if (
                result.status === "success" &&
                result.data?.deleteBlooketQuestionSet.error === null
            ) {
                closeModal(true);
            } else if (result.status === "error") {
                renderErrorToast(
                    t`Oops! Something went wrong. Please try again.`,
                );
                closeModal(false);
            }
        } else {
            const result = await archiveDocumentMutation({threadID});

            if (
                result.status === "success" &&
                result.data?.archiveCoeditingDocument?.error === null
            ) {
                closeModal(true);
            } else if (result.status === "error") {
                renderErrorToast(
                    t`Oops! Something went wrong. Please try again.`,
                );
                closeModal(false);
            }
        }
    };

    const handleCancel = () => {
        closeModal(false);
    };

    const title = t`Are you sure?`;
    const bodyText = t`Are you sure you want to delete "${documentName}"?`;

    return (
        <OnePaneDialog
            closeButtonVisible={true}
            testId="my-documents-archive-modal"
            style={styles.modal}
            title={title}
            content={<Body>{bodyText}</Body>}
            footer={
                <View style={styles.footer}>
                    <Button
                        testId="my-documents-archive-cancel"
                        kind="tertiary"
                        onClick={handleCancel}
                    >
                        {t`Cancel`}
                    </Button>
                    <Strut size={spacing.medium_16} />
                    <Button
                        testId="my-documents-archive-confirm"
                        onClick={() => handleArchive(threadId, configName)}
                        actionType="destructive"
                    >
                        {t`Delete`}
                    </Button>
                </View>
            }
        />
    );
}

const styles = StyleSheet.create({
    footer: {
        flexDirection: "row",
    },
    modal: {
        width: 576,
        height: 252,
    },
});

export default MyDocumentsArchiveModal;
